import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Announcement, AnnouncementService} from "../../../../services/announcement.service";
import {ConfirmComponent} from "../../../global/dialog/confirm/confirm.component";
import {filter, tap} from "rxjs/operators";
import {Subscription, switchMap} from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {SnackbarService} from "src/app/services/front/snackbar.service";
import {AnnouncementPreviewComponent} from "../announcement-preview/announcement-preview.component";
import {DesignSystemModule} from "../../../design-system/design-system.module";
import {CommonModule} from "@angular/common";
import {MiniButtonModule} from "../../../global/button/mini-button/mini-button.module";
import {TabsWidgetComponent} from "../../../design-system/tabs-widget/tabs-widget.component";
import {TabWidgetComponent} from "../../../design-system/tabs-widget/tab-widget.component";
import {AnnouncementLineComponent} from "./announcement-line.component";
import {CurrentTenantService} from "../../../../services/front/current-tenant.service";
import {ButtonWidgetComponent} from "../../../design-system/button-widget/button-widget.component";

@Component({
  selector: 'app-announcement-list',
  standalone: true,
  templateUrl: './announcement-list.component.html',
	imports: [
		DesignSystemModule,
		TranslateModule,
		CommonModule,
		MiniButtonModule,
		TabsWidgetComponent,
		TabWidgetComponent,
		AnnouncementLineComponent,
		ButtonWidgetComponent
	],
  styleUrls: ['./announcement-list.component.scss']
})
export class AnnouncementListComponent implements OnInit {
  @Input() announcements: Announcement[];
  @Input() tenantId: string;
  protected programmedAnnouncements: Announcement[] = [];
  protected activeAnnouncements: Announcement[] = [];
  protected archiveAnnouncements: Announcement[] = [];

  isReadonly: boolean = false;

  subscription: Subscription = new Subscription();

  constructor(private dialog: MatDialog,
			  private currentTenantService: CurrentTenantService,
              private translate: TranslateService,
              private announcementService: AnnouncementService,
              private snackbarService: SnackbarService) {
  }

  ngOnInit() {
    this.sortAnnouncements(this.announcements);
	this.subscription.add(this.currentTenantService.getCurrentTenantChanges()
		.subscribe(tenant => {
			this.isReadonly = tenant.configuration.role === 'read_only'
		}));
  }

  protected sortAnnouncements(announcements: Announcement[]) {
      const sortedAnnouncement = announcements.sort((a,b) => a?.startDate > b?.startDate ? -1 : 1)
      this.activeAnnouncements = sortedAnnouncement.filter(a => {
        if (a.endDate) {
          return new Date(a.startDate).getTime() <= new Date().getTime() && new Date(a.endDate).getTime() > new Date().getTime()
        } else {
          return new Date(a.startDate).getTime() <= new Date().getTime()
        }
      })

      this.archiveAnnouncements = sortedAnnouncement.filter(a => {
        if (a.endDate) {
          return new Date(a.endDate).getTime() < new Date().getTime()
        } else {
          return false
        }
      });

      this.programmedAnnouncements = sortedAnnouncement.filter(a => {
        if (a.endDate) {
          return  new Date(a.startDate).getTime() > new Date().getTime() && new Date(a.endDate).getTime() < new Date().getTime()
        } else {
          return  new Date(a.startDate).getTime() > new Date().getTime()

        }
      });
  }

  deleteAnnouncement(announcementId: string): void {
      const data = {
        title: '',
        message: this.translate.instant('confirmModal.deleteAnnouncement'),
        confirmButton: this.translate.instant('button.delete'),
        closeButton: this.translate.instant('button.cancel')
      };
      this.dialog.open(ConfirmComponent, {data: data})
        .afterClosed()
        .pipe(
          filter(result => result),
          tap(),
          switchMap(() => this.announcementService.deleteAnnouncement(this.tenantId, announcementId)))
        .subscribe(() => {
          this.announcements = this.announcements.filter(a => a.id !== announcementId);
          this.sortAnnouncements(this.announcements);
          this.snackbarService.show(this.translate.instant('global.announcements.announcementDeleted'))
        });
  }

  openCreateAnnouncement() {
    this.dialog.open(AnnouncementPreviewComponent,  {data: {}})
      .afterClosed()
      .pipe(
        filter(result => result && result.success),
        tap(() => this.snackbarService.show(this.translate.instant('global.announcements.announcementCreated'))),
        switchMap(() => this.announcementService.getAnnouncements(this.tenantId))
      )
      .subscribe((announcements) => {
        this.announcements = announcements.map(announcement => {
          announcement.createdAt = new Date(announcement.createdAt)
          announcement.updatedAt = new Date(announcement.updatedAt)
			announcement.startDate = new Date(announcement.startDate)

          let teamsTooltip: {
            id: string
            parentName: string
            teams: {
              name: string
            }[]
          }[] = []

          announcement.teams?.forEach(team => {
            const parent = teamsTooltip.find(parent => parent.id === team.parentId)
            if (parent) {
              teamsTooltip.find(parent => parent.id === team.parentId)?.teams.push({name: team.name})
            } else {
              teamsTooltip.push({
                id: team.parentId,
                parentName: team.parentName,
                teams: [{name: team.name}]
              })
            }
          })
          announcement.teamsTooltip = teamsTooltip
          return announcement
        });

        this.sortAnnouncements(this.announcements);
      });
  }

  openAnnouncement(announcement: { announcement: Announcement, mode: 'read' | 'write' }) {
      this.dialog.open(AnnouncementPreviewComponent, {data: {announcement: announcement.announcement, mode: announcement.mode}})
        .afterClosed()
        .pipe(
          filter(result => result && result.success ),
          tap(() => this.snackbarService.show(this.translate.instant('global.announcements.announcementUpdated'))),
					switchMap(() => this.announcementService.getAnnouncements(this.tenantId))
        ).subscribe((announcements) => {
            this.announcements = announcements.map(announcement => {
              announcement.createdAt = new Date(announcement.createdAt)
              announcement.updatedAt = new Date(announcement.updatedAt)
				announcement.startDate = new Date(announcement.startDate)

              let teamsTooltip: {
                id: string
                parentName: string
                teams: {
                  name: string
                }[]
              }[] = []

              // announcement.teamsTooltip = teams group by parentId
              announcement.teams?.forEach(team => {
                const parent = teamsTooltip.find(parent => parent.id === team.parentId)
                if (parent) {
                  teamsTooltip.find(parent => parent.id === team.parentId)?.teams.push({name: team.name})
                } else {
                  teamsTooltip.push({
                    id: team.parentId,
                    parentName: team.parentName,
                    teams: [{name: team.name}]
                  })
                }
              })
              announcement.teamsTooltip = teamsTooltip
              return announcement
            });
				});
  }
}
